import React, { useState, useEffect } from 'react';
import { getVendors } from '../services/IngredientsService';
import './IngredientsManagement.css'; // Reuse the same styling

const VendorsManagement = () => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingVendor, setEditingVendor] = useState(null);
  const [newVendor, setNewVendor] = useState({
    company: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('company');
  const [sortDirection, setSortDirection] = useState('asc');

  // Load vendors on component mount
  useEffect(() => {
    const loadVendors = async () => {
      try {
        setLoading(true);
        const data = await getVendors();
        setVendors(data);
        setError('');
      } catch (error) {
        console.error('Error loading vendors:', error);
        setError('Failed to load vendors. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadVendors();
  }, []);

  // Handle input change for new vendor form
  const handleNewVendorChange = (e) => {
    const { name, value } = e.target;
    setNewVendor({
      ...newVendor,
      [name]: value
    });
  };

  // Handle input change for editing vendor
  const handleEditVendorChange = (e) => {
    const { name, value } = e.target;
    setEditingVendor({
      ...editingVendor,
      [name]: value
    });
  };

  // Get auth token from localStorage
  const getAuthToken = () => {
    return localStorage.getItem('token');
  };

  // API base URL
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

  // Handle adding a new vendor
  const handleAddVendor = async (e) => {
    e.preventDefault();
    
    if (!newVendor.company) {
      setError('Company name is required');
      return;
    }
    
    try {
      setLoading(true);
      
      const token = getAuthToken();
      if (!token) {
        throw new Error('Authentication token not found');
      }
      
      const response = await fetch(`${API_BASE_URL}/api/vendors`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newVendor)
      });
      
      if (!response.ok) {
        throw new Error(`Failed to add vendor: ${response.statusText}`);
      }
      
      const addedVendor = await response.json();
      setVendors([...vendors, addedVendor]);
      setNewVendor({
        company: '',
        first_name: '',
        last_name: '',
        email: '',
        phone: ''
      });
      setError('');
    } catch (error) {
      console.error('Error adding vendor:', error);
      setError('Failed to add vendor. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle updating a vendor
  const handleUpdateVendor = async (e) => {
    e.preventDefault();
    
    if (!editingVendor.company) {
      setError('Company name is required');
      return;
    }
    
    try {
      setLoading(true);
      
      const token = getAuthToken();
      if (!token) {
        throw new Error('Authentication token not found');
      }
      
      const response = await fetch(`${API_BASE_URL}/api/vendors/${editingVendor.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editingVendor)
      });
      
      if (!response.ok) {
        throw new Error(`Failed to update vendor: ${response.statusText}`);
      }
      
      const updatedVendor = await response.json();
      
      setVendors(vendors.map(vendor => 
        vendor.id === editingVendor.id ? updatedVendor : vendor
      ));
      
      setEditingVendor(null);
      setError('');
    } catch (error) {
      console.error('Error updating vendor:', error);
      setError('Failed to update vendor. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle deleting a vendor
  const handleDeleteVendor = async (id) => {
    if (!window.confirm('Are you sure you want to delete this vendor?')) {
      return;
    }
    
    try {
      setLoading(true);
      
      const token = getAuthToken();
      if (!token) {
        throw new Error('Authentication token not found');
      }
      
      const response = await fetch(`${API_BASE_URL}/api/vendors/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to delete vendor: ${response.statusText}`);
      }
      
      setVendors(vendors.filter(vendor => vendor.id !== id));
      setError('');
    } catch (error) {
      console.error('Error deleting vendor:', error);
      setError('Failed to delete vendor. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle sort change
  const handleSortChange = (field) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Filter vendors based on search term
  const filteredVendors = vendors.filter(vendor => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (vendor.company && vendor.company.toLowerCase().includes(searchLower)) ||
      (vendor.first_name && vendor.first_name.toLowerCase().includes(searchLower)) ||
      (vendor.last_name && vendor.last_name.toLowerCase().includes(searchLower)) ||
      (vendor.email && vendor.email.toLowerCase().includes(searchLower)) ||
      (vendor.phone && vendor.phone.toLowerCase().includes(searchLower))
    );
  });

  // Sort vendors based on sort field and direction
  const sortedVendors = [...filteredVendors].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];
    
    // String comparison
    aValue = String(aValue || '').toLowerCase();
    bValue = String(bValue || '').toLowerCase();
    
    if (sortDirection === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  // Get sort indicator
  const getSortIndicator = (field) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? '▲' : '▼';
  };

  return (
    <div className="ingredients-management">
      <div className="ingredients-management-header">
        <h2>Vendors Management</h2>
        <p>Manage vendors for proprietary chemical information</p>
      </div>
      
      {error && <div className="error-message">{error}</div>}
      
      <div className="ingredients-controls">
        <div className="search-container">
          <input
            type="text"
            placeholder="Search vendors..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="search-input"
          />
        </div>
      </div>
      
      {loading ? (
        <div className="loading-message">Loading vendors...</div>
      ) : (
        <>
          <div className="ingredients-table-container">
            <table className="ingredients-table">
              <thead>
                <tr>
                  <th onClick={() => handleSortChange('company')}>
                    Company {getSortIndicator('company')}
                  </th>
                  <th onClick={() => handleSortChange('first_name')}>
                    First Name {getSortIndicator('first_name')}
                  </th>
                  <th onClick={() => handleSortChange('last_name')}>
                    Last Name {getSortIndicator('last_name')}
                  </th>
                  <th onClick={() => handleSortChange('email')}>
                    Email {getSortIndicator('email')}
                  </th>
                  <th onClick={() => handleSortChange('phone')}>
                    Phone {getSortIndicator('phone')}
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedVendors.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="no-ingredients">
                      No vendors found. Add some below.
                    </td>
                  </tr>
                ) : (
                  sortedVendors.map((vendor) => (
                    <tr key={vendor.id}>
                      <td>{vendor.company}</td>
                      <td>{vendor.first_name}</td>
                      <td>{vendor.last_name}</td>
                      <td>{vendor.email}</td>
                      <td>{vendor.phone}</td>
                      <td className="action-buttons">
                        <button 
                          className="edit-button"
                          onClick={() => setEditingVendor(vendor)}
                          title="Edit Vendor"
                        >
                          Edit
                        </button>
                        <button 
                          className="delete-button"
                          onClick={() => handleDeleteVendor(vendor.id)}
                          title="Delete Vendor"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          <div className="add-ingredient-form">
            <h3>Add New Vendor</h3>
            <form onSubmit={handleAddVendor}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="company">Company *</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={newVendor.company}
                    onChange={handleNewVendorChange}
                    required
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="first_name">First Name</label>
                  <input
                    type="text"
                    id="first_name"
                    name="first_name"
                    value={newVendor.first_name}
                    onChange={handleNewVendorChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="last_name">Last Name</label>
                  <input
                    type="text"
                    id="last_name"
                    name="last_name"
                    value={newVendor.last_name}
                    onChange={handleNewVendorChange}
                  />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={newVendor.email}
                    onChange={handleNewVendorChange}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="phone">Phone</label>
                  <input
                    type="text"
                    id="phone"
                    name="phone"
                    value={newVendor.phone}
                    onChange={handleNewVendorChange}
                    placeholder="555-123-4567"
                  />
                </div>
              </div>
              
              <div className="form-actions">
                <button type="submit" className="add-button">
                  Add Vendor
                </button>
              </div>
            </form>
          </div>
        </>
      )}
      
      {editingVendor && (
        <div className="edit-ingredient-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Edit Vendor</h3>
              <button 
                className="close-button"
                onClick={() => setEditingVendor(null)}
              >
                &times;
              </button>
            </div>
            
            <div className="modal-body">
              <form onSubmit={handleUpdateVendor}>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="edit-company">Company *</label>
                    <input
                      type="text"
                      id="edit-company"
                      name="company"
                      value={editingVendor.company || ''}
                      onChange={handleEditVendorChange}
                      required
                    />
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="edit-first_name">First Name</label>
                    <input
                      type="text"
                      id="edit-first_name"
                      name="first_name"
                      value={editingVendor.first_name || ''}
                      onChange={handleEditVendorChange}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="edit-last_name">Last Name</label>
                    <input
                      type="text"
                      id="edit-last_name"
                      name="last_name"
                      value={editingVendor.last_name || ''}
                      onChange={handleEditVendorChange}
                    />
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="edit-email">Email</label>
                    <input
                      type="email"
                      id="edit-email"
                      name="email"
                      value={editingVendor.email || ''}
                      onChange={handleEditVendorChange}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="edit-phone">Phone</label>
                    <input
                      type="text"
                      id="edit-phone"
                      name="phone"
                      value={editingVendor.phone || ''}
                      onChange={handleEditVendorChange}
                      placeholder="555-123-4567"
                    />
                  </div>
                </div>
                
                <div className="form-actions">
                  <button type="button" className="cancel-button" onClick={() => setEditingVendor(null)}>
                    Cancel
                  </button>
                  <button type="submit" className="save-button">
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorsManagement;
